// src/screens/s2/BusinessTrends.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 4 — Business Trends.
// 2×2 grid of charts sourced from /api/xero/sales-analysis: monthly revenue
// with FY dividers; retention-rate trend; average client fee trend; new vs
// churned clients per FY. Auto-flags below. Optional fifth chart when
// tracking categories are enabled on the Xero tenant.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine, LineChart, BarChart, Legend,
} from 'recharts';
import { ClientData } from '../../types/ClientData';
import { useXeroSales } from '../../hooks/useXeroSales';
import { formatCurrency, formatPercent } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

// Auto-detected risk/opportunity flag shown in the summary panel.
interface Flag {
  tone: 'warn' | 'info' | 'good';
  message: string;
}

const FY_BOUNDARY_MONTH = 4;   // UK April year-end → first month of each FY is 04
const CAT_COLOURS = ['#1E3A5F', '#60A5FA', '#10B981', '#C9A84C', '#DC2626', '#8B5CF6'];

function ChartCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-1">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      <div style={{ width: '100%', height: 240 }}>{children}</div>
    </div>
  );
}

export default function BusinessTrends({ data }: Props) {
  const sales = useXeroSales(data.xeroTenantId);

  if (sales.loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="text-sm text-gray-500">Loading sales history from Xero…</div>
      </div>
    );
  }
  if (!sales.data) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-ma-navy mb-4">Business Trends</h2>
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 text-sm text-amber-800">
          {sales.error
            ? <>Xero unavailable ({sales.error}). This screen requires 24 months of invoice history.</>
            : <>No sales history available.</>}
        </div>
      </div>
    );
  }

  const d = sales.data;

  // ── Monthly revenue with FY-boundary markers ────────────────────────────
  // Detect each month whose FIRST month of the FY corresponds to a boundary,
  // so we can drop a dashed vertical marker into the revenue chart.
  const fyBoundaryIndices: number[] = [];
  d.monthlyRevenue.forEach((m, i) => {
    const month = parseInt(m.month.slice(5, 7), 10);
    if (month === FY_BOUNDARY_MONTH && i > 0) fyBoundaryIndices.push(i);
  });

  // ── Yearly KPI arrays (retention, avg fee, new/churned) ────────────────
  const yearlyKPIs = d.yearlyKPIs || [];
  const retentionSeries = yearlyKPIs.map((y) => ({ year: y.year, retentionRate: y.retentionRate }));
  const feeSeries = yearlyKPIs.map((y) => ({ year: y.year, avgClientFee: y.avgClientFee }));
  const newChurnedSeries = yearlyKPIs.map((y) => ({
    year: y.year,
    newClients: y.newClients ?? 0,
    churnedClients: -(y.churnedClients ?? 0),   // negative so the bar drops below axis
  }));

  // ── Auto-generated flags ────────────────────────────────────────────────
  const flags: Flag[] = [];
  if (yearlyKPIs.length >= 2) {
    const recent = yearlyKPIs[yearlyKPIs.length - 2];       // last COMPLETE FY
    const prior = yearlyKPIs[yearlyKPIs.length - 3] || recent;
    if (recent.revenue > prior.revenue * 1.05) flags.push({ tone: 'good', message: `Revenue growing — up ${Math.round(((recent.revenue / prior.revenue) - 1) * 100)}% vs prior FY.` });
    else if (recent.revenue < prior.revenue * 0.95) flags.push({ tone: 'warn', message: `Revenue declining — down ${Math.round((1 - (recent.revenue / prior.revenue)) * 100)}% vs prior FY.` });
    if (recent.grossMargin < prior.grossMargin) flags.push({ tone: 'warn', message: `Margin compression — gross margin fell from ${prior.grossMargin}% to ${recent.grossMargin}%.` });
    if (recent.overheadRatio > prior.overheadRatio) flags.push({ tone: 'warn', message: `Overheads trending up — ratio rose from ${prior.overheadRatio}% to ${recent.overheadRatio}% of revenue.` });
    if (recent.retentionRate != null && prior.retentionRate != null && recent.retentionRate < prior.retentionRate) {
      flags.push({ tone: 'warn', message: `Retention declining — fell from ${prior.retentionRate}% to ${recent.retentionRate}%.` });
    }
  }
  // Revenue concentration flag: top client > 20% of total revenue in last 12m.
  if (d.clientDetail.length > 0) {
    const topClient = d.clientDetail[0];
    const recent12Revenue = d.monthlyRevenue.slice(12).reduce((s, m) => s + m.revenue, 0);
    if (recent12Revenue > 0 && topClient.totalRevenue / 2 / recent12Revenue > 0.2) {
      flags.push({ tone: 'warn', message: `Concentration risk — ${topClient.name} is >20% of recent revenue.` });
    }
  }

  const flagColour = (tone: Flag['tone']) =>
    tone === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800'
      : tone === 'good' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
      : 'bg-blue-50 border-blue-200 text-blue-800';

  // ── Tracking categories (optional fifth chart) ──────────────────────────
  const tracking = d.trackingCategories;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* HEADER */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">
          Business Trends
          {sales.tenantName && <span className="ml-2 text-sm font-normal text-gray-500">— {sales.tenantName}</span>}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          24 months of sales history, by month and by financial year.
        </p>
      </div>

      {/* FLAGS PANEL */}
      {flags.length > 0 && (
        <div className="mb-6 space-y-2">
          {flags.map((f, i) => (
            <div key={i} className={`rounded-xl border p-3 text-sm ${flagColour(f.tone)}`}>
              {f.message}
            </div>
          ))}
        </div>
      )}

      {/* 2x2 GRID OF CHARTS */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Chart 1: monthly revenue with FY boundaries */}
        <ChartCard
          title="Monthly revenue"
          subtitle="24 months. Dashed lines mark FY boundaries."
        >
          <ResponsiveContainer>
            <ComposedChart data={d.monthlyRevenue} margin={{ top: 10, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 9 }} stroke="#9CA3AF" interval={2} />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <Bar dataKey="revenue" fill="#1E3A5F" radius={[2, 2, 0, 0]} />
              {fyBoundaryIndices.map((idx) => (
                <ReferenceLine
                  key={idx}
                  x={d.monthlyRevenue[idx].month}
                  stroke="#C9A84C"
                  strokeDasharray="4 2"
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Chart 2: retention rate by FY */}
        <ChartCard
          title="Retention rate"
          subtitle="Per financial year. Null values are omitted (prior cohort outside window)."
        >
          <ResponsiveContainer>
            <LineChart data={retentionSeries.filter((r) => r.retentionRate != null)} margin={{ top: 10, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: any) => `${v}%`} />
              <Line
                type="monotone"
                dataKey="retentionRate"
                stroke="#10B981"
                strokeWidth={3}
                dot={{ r: 5, fill: '#10B981' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Chart 3: average client fee by FY */}
        <ChartCard
          title="Average client fee"
          subtitle="Per-FY revenue ÷ active clients that year."
        >
          <ResponsiveContainer>
            <LineChart data={feeSeries} margin={{ top: 10, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <Line
                type="monotone"
                dataKey="avgClientFee"
                stroke="#C9A84C"
                strokeWidth={3}
                dot={{ r: 5, fill: '#C9A84C' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Chart 4: new vs churned clients per FY */}
        <ChartCard
          title="New vs churned clients"
          subtitle="Positive bars = new. Negative bars = churned."
        >
          <ResponsiveContainer>
            <BarChart data={newChurnedSeries} margin={{ top: 10, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <Tooltip formatter={(v: any) => Math.abs(Number(v))} />
              <ReferenceLine y={0} stroke="#9CA3AF" />
              <Bar dataKey="newClients" fill="#10B981" radius={[2, 2, 0, 0]} />
              <Bar dataKey="churnedClients" fill="#DC2626" radius={[0, 0, 2, 2]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* TRACKING CATEGORIES — CONDITIONAL FIFTH CHART */}
      {tracking && tracking.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
            Revenue by {tracking[0].name.toLowerCase()}
          </h3>
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <BarChart
                  data={[{ segment: tracking[0].name, ...Object.fromEntries(tracking[0].options.map((o) => [o.name, o.revenue])) }]}
                  layout="vertical"
                  margin={{ top: 10, right: 20, bottom: 8, left: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
                  <YAxis type="category" dataKey="segment" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                  <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {tracking[0].options.map((opt, i) => (
                    <Bar
                      key={opt.name}
                      dataKey={opt.name}
                      stackId="segment"
                      fill={CAT_COLOURS[i % CAT_COLOURS.length]}
                      radius={i === tracking[0].options.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* HEADLINE METRICS */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Active clients</div>
          <div className="text-2xl font-bold text-ma-navy">{d.activeClients}</div>
          <div className="text-xs text-gray-500 mt-1">Last 12 months</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Avg fee / client</div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(d.avgFeePerClient)}</div>
          <div className="text-xs text-gray-500 mt-1">Annualised</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Retention</div>
          <div className="text-2xl font-bold text-ma-navy">{formatPercent(d.retentionRate)}</div>
          <div className="text-xs text-gray-500 mt-1">Rolling 12m cohort</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">New / churned (12m)</div>
          <div className="text-2xl font-bold text-ma-navy">
            <span className="text-emerald-700">{d.newClientsLast12m}</span>
            <span className="text-gray-400 mx-1">/</span>
            <span className="text-red-700">{d.churnedLast12m}</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">Gained vs lost</div>
        </div>
      </div>
    </div>
  );
}
